import { motion } from "framer-motion";
import Navigation from "../../components/Navigation";
import PageHero from "../../components/PageHero";

// Board Member Card Component
const BoardMemberCard = ({ name, title, pronouns, bio, image }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {image && (
        <div className="w-full h-48 overflow-hidden">
          <img 
            src={image} 
            alt={name}
            className="w-full h-full object-cover object-top"
          />
        </div>
      )}
      <div className="p-5">
        <h3 
          className="font-bold text-[#1A1A1A] text-lg mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {name}
        </h3>
        <p className="text-[#C41E3A] font-medium text-sm mb-1">{title}</p>
        {pronouns && <p className="text-[#999999] text-xs mb-3">({pronouns})</p>}
        {bio && <p className="text-[#666666] text-sm leading-relaxed">{bio}</p>}
      </div>
    </div>
  );
};

// Board Page
const BoardPage = () => {
  const president = {
    name: "Raymond Baraldi",
    title: "Interim President",
    pronouns: "he/him",
    bio: "Ray Baraldi is a retired physician, having worked previously as Department Chair of Radiology and Chief Medical Officer at two local hospitals. He volunteers at Murphy's Giving Market at every food distribution and has served on the board since its inception.",
    image: "https://customer-assets.emergentagent.com/job_4bb091c1-e4be-474e-9067-19b51e555ba6/artifacts/hw7j3tqz_Ray%20Baraldi.jpg"
  };

  const boardMembers = [
    {
      name: "Guy T. Dunn",
      title: "Vice President",
      pronouns: "he/him",
      bio: "Guy Dunn is lifelong entrepreneur and consultant. He has over 30 years of business ownership experience as well as being a former CEO, CFO, COO, and Director of Business Development.",
      image: null
    },
    {
      name: "Jacquelynn Williams",
      title: "Secretary",
      pronouns: "she/her",
      bio: "Jacquelynn Williams is a licensed nursing home administrator and certified professional in risk management with 30+ years of management and operational experience in aging services. She works at Foulkeways at Gwynedd.",
      image: null
    },
    {
      name: "Bruce Flamm",
      title: "Treasurer",
      pronouns: "he/him",
      bio: "Bruce Flamm is a retired financial manager and CPA who was WHYY's Chief Operating Officer and Chief Financial Officer. He has served on several nonprofit boards, including the Chester County Community Foundation and Family Services of Chester County.",
      image: null
    },
    {
      name: "Clementa Amazan",
      title: "Board Member",
      pronouns: "she/her",
      bio: "Clementa Amazan is an attorney with eight years of experience in commercial real estate and business transactions. She represents property owners, developers, lenders, and businesses across a range of matters including real estate financings, business formation, and mergers and acquisitions.",
      image: null
    },
    {
      name: "Michael Atwell",
      title: "Board Member",
      pronouns: "he/him",
      bio: "Mike Atwell is a finance and management professional with 25+ years' experience in reporting, analytics, and risk management. He works as a corporate finance and management consultant.",
      image: null
    },
    {
      name: "Ellie Crowell",
      title: "Board Member",
      pronouns: "they/them",
      bio: "Ellie Crowell is an independent consultant and grassroots organizer with a decade of experience in nonprofit, cooperative, and movement-based community organizations. From 2019–2025, they worked at Share Food Program, first managing warehouse operations and later directing the expansion of its programming in Delaware County.",
      image: null
    },
    {
      name: "Kristine DelMonte",
      title: "Board Member",
      pronouns: "she/her",
      bio: "Kristine DelMonte is a nonprofit development professional with 20+ years of experience in social services nonprofits, specializing in fundraising strategy, marketing, donor engagement, and corporate and volunteer partnerships. She serves as Chief Development & Marketing Officer at Easterseals of Southeastern Pennsylvania.",
      image: null
    },
    {
      name: "Rashida L. Ximines",
      title: "Board Member",
      pronouns: "she/her",
      bio: "Rashida Ximines works in Human Resources for the City of Philadelphia and is a real estate professional. She has volunteered with Murphy's Giving Market from the very start.",
      image: null
    }
  ];

  return (
    <div data-testid="board-page" className="min-h-screen bg-[#FAFAFA]">
      <Navigation />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/imavki54_National%20Night%20Out.jpg"
        alt="Murphy's Giving Market Board"
        objectPosition="center 25%"
      />

      {/* Board Content */}
      <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 
              data-testid="board-title"
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-12"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Board
            </h1>
            
            {/* President - Centered at top with full photo */}
            <div className="flex justify-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden w-full max-w-[300px]"
              >
                <div className="w-full">
                  <img 
                    src={president.image} 
                    alt={president.name}
                    className="w-full h-auto"
                  />
                </div>
                <div className="p-5 text-left">
                  <h3 
                    className="font-bold text-[#1A1A1A] text-lg mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {president.name}
                  </h3>
                  <p className="text-[#C41E3A] font-medium text-sm mb-1">{president.title}</p>
                  <p className="text-[#999999] text-xs mb-3">({president.pronouns})</p>
                  <p className="text-[#666666] text-sm leading-relaxed">{president.bio}</p>
                </div>
              </motion.div>
            </div>
            
            {/* Other Board Members */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {boardMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <BoardMemberCard {...member} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BoardPage;
